import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Award, ArrowRight, Trophy, Star, MapPin, Crown, Users, ScrollText, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import AwardCard from '../components/AwardCard';
import TierBadge from '../components/TierBadge';
import { CATEGORIES_2021 } from '../data/winners2021';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The 5 TWBL category IDs whose winners should be promoted on the home page
const TWBL_CATEGORY_IDS = [
  'luxury-destination',
  'luxury-accommodation',
  'luxury-air',
  'luxury-experience',
  'luxury-b2b',
];

const FALLBACK_TWBL_IMAGE = {
  // Maldives — iconic overwater villas + turquoise lagoon
  'luxury-destination':   'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=1200&q=80&auto=format&fit=crop',
  'luxury-accommodation': 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=1200&q=80&auto=format&fit=crop',
  // VistaJet — actual Bombardier Global jet in VistaJet livery (Wikimedia)
  'luxury-air':           'https://commons.wikimedia.org/wiki/Special:FilePath/Vista%20Jet%20Bombardier%20BD-700-1A10%20Global%20Express%20XRS%3B%20OE-LXR%40ZRH%3B08.06.2011%20(5832393313)%20(cropped).jpg?width=1200',
  'luxury-experience':    'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1200&q=80&auto=format&fit=crop',
  'luxury-b2b':           'https://www.alhi.com/includes/client_public/assets/shared/logos/footer-logo.svg',
};

export default function HomePage() {
  const [featuredAwards, setFeaturedAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedAwards();
  }, []);

  const fetchFeaturedAwards = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/awards`);
      const all = response.data || [];
      const byName = {};
      for (const a of all) {
        if (a?.name) byName[a.name.toLowerCase().trim()] = a;
      }

      // ===== Curated Travel Awards (gold winners across the program) =====
      const FEATURED_TRAVEL_NAMES = ['IHG Hotels', 'Los Cabos Tourism'];
      const travelTop3 = FEATURED_TRAVEL_NAMES
        .map((n) => byName[n.toLowerCase().trim()])
        .filter(Boolean)
        .map((a) => ({ ...a, _source: 'travel' }));

      // ===== 3 TWBL winners (Destination, Accommodation, Air) — synthesize cards =====
      const twblCategories = CATEGORIES_2021.filter((c) => TWBL_CATEGORY_IDS.includes(c.id));
      const twblTop3 = twblCategories.slice(0, 3).map((cat) => {
        const winnerName = cat.winner.name;
        const matched = byName[winnerName.toLowerCase().trim()];
        return {
          id: `twbl-${cat.id}`,
          name: winnerName,
          category: cat.id.replace('luxury-', '') || 'luxury',
          location: matched?.location || cat.title.replace("World's Best Luxury ", ''),
          country: matched?.country || '',
          description: matched?.description || `World's Best Luxury ${cat.title.replace("World's Best Luxury ", '')} 2021.`,
          star_rating: matched?.star_rating || 5,
          tier: 'gold',
          auto_tier: 'gold',
          final_score: matched?.final_score,
          image_url: matched?.image_url || FALLBACK_TWBL_IMAGE[cat.id],
          sub_award: 'TWBL Award Winner',
          _source: 'twbl',
          _href: matched ? `/awards/${matched.id}` : '/twblawards',
        };
      });

      setFeaturedAwards([...travelTop3, ...twblTop3]);
    } catch (error) {
      console.error('Error fetching awards:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1761437855598-011cf89b2ad4?w=1920&q=80&auto=format&fit=crop"
            alt="Dark luxury gold abstract background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {/* Free Nominations CTA */}
            <Link
              to="/nominate"
              className="group inline-flex items-center gap-3 mb-6 px-4 py-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 transition-all animate-fade-in-up"
              data-testid="hero-nomination-cta"
            >
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.18em] shadow-[0_0_12px_rgba(212,175,55,0.4)]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A] animate-pulse" />
                Now Open
              </span>
              <span className="text-white/90 text-sm font-medium">
                Free Nominations — 2027 Travel Awards & TWBL Awards
              </span>
              <ArrowRight className="w-4 h-4 text-[#D4AF37] transition-transform group-hover:translate-x-1" />
            </Link>

            {/* Overline */}
            <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-6 animate-fade-in-up">
              World's Premier Travel Recognition
            </p>

            {/* Main Heading */}
            <h1 className="font-['Playfair_Display'] text-5xl sm:text-6xl lg:text-7xl font-medium text-white mb-6 leading-tight animate-fade-in-up stagger-1">
              Celebrating{' '}
              <span className="text-gradient-gold">Excellence</span>{' '}
              in Luxury Travel
            </h1>

            {/* Subheading */}
            <p className="text-lg sm:text-xl text-white/70 mb-10 leading-relaxed animate-fade-in-up stagger-2">
              Discover the world's finest hotels, resorts, and destinations as voted by discerning travelers and industry professionals.
            </p>
          </div>
        </div>

        {/* Award Badge Decoration */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden xl:block">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/62e20e7e-ef6c-47dc-9d89-90583952dc0a/images/de1cfdf4144cd7031fb3bb76c0f1b1e15c0ea01b2c4188dcd0d21408760ed638.png"
            alt="Award badge"
            className="w-80 h-80 object-contain opacity-30"
          />
        </div>
      </section>

      {/* Nominations Promotional Header */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0A0A0A] border-t border-white/5" data-testid="nomination-promo">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-4 flex items-center justify-center gap-2">
              <Crown className="w-4 h-4" />
              2027 Travel Awards & TWBL Awards
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4 leading-tight whitespace-nowrap">
              <span className="text-gradient-gold">Nominations Now Open</span>
              <span className="text-white/70 text-xl sm:text-2xl lg:text-3xl font-normal align-middle ml-3">— Free</span>
            </h2>
            <p className="text-white/60 leading-relaxed">
              Submit your organization, explore who can be nominated, and discover the
              transparent methodology that powers the world&apos;s premier travel awards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link
              to="/nominate"
              className="group relative overflow-hidden rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#1A1408] via-[#0F0F0F] to-[#0A0A0A] p-8 transition-all hover:border-[#D4AF37]/70 hover:shadow-[0_0_60px_rgba(212,175,55,0.18)] flex flex-col"
              data-testid="promo-nominate"
            >
              <div className="absolute -top-32 -right-32 w-80 h-80 bg-[#D4AF37]/10 rounded-full blur-3xl group-hover:bg-[#D4AF37]/20 transition-all" />
              <div className="relative flex-1 flex flex-col">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.18em] mb-5 self-start shadow-[0_0_12px_rgba(212,175,55,0.4)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A] animate-pulse" />
                  Now Open · Free
                </div>
                <Sparkles className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white mb-3">
                  Submit a Nomination
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">
                  Nominations are open at no cost. Submit your organization across
                  Hotel · Resort · Villa, Air · Cruise · B2B, or Destination categories.
                </p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold text-sm group-hover:gap-3 transition-all">
                  Nominate Now
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            <Link
              to="/nominate"
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#121212] p-8 transition-all hover:border-[#D4AF37]/40 flex flex-col"
              data-testid="promo-who"
            >
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/5 rounded-full blur-3xl group-hover:bg-[#D4AF37]/5 transition-all" />
              <div className="relative flex-1 flex flex-col">
                <p className="uppercase text-[10px] tracking-[0.3em] text-white/40 mb-5">
                  Eligibility
                </p>
                <Users className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white mb-3">
                  Who Can Nominate
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">
                  Hotels, resorts, villas, airlines, cruise operators, tour operators,
                  tourism boards, B2B brands, hospitality professionals — and travelers
                  may submit third-party nominations.
                </p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold text-sm group-hover:gap-3 transition-all">
                  See Full List
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            <Link
              to="/methodology"
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#121212] p-8 transition-all hover:border-[#D4AF37]/40 flex flex-col"
              data-testid="promo-methodology"
            >
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/5 rounded-full blur-3xl group-hover:bg-[#D4AF37]/5 transition-all" />
              <div className="relative flex-1 flex flex-col">
                <p className="uppercase text-[10px] tracking-[0.3em] text-white/40 mb-5">
                  Transparent Framework
                </p>
                <ScrollText className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white mb-3">
                  Our Methodology
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6 flex-1">
                  Final Score = Public 15% + Pro Partners 20% + Jury 30% + TAO 35%.
                  Auto-tiered: Gold 90+, Silver 80+, Bronze 70+. Independent, audited,
                  bias-resistant.
                </p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold text-sm group-hover:gap-3 transition-all">
                  Read Methodology
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Awards Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-3 flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                Travel Awards & TWBL Awards
              </p>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white">
                Featured Award Winners
              </h2>
            </div>
            <Link 
              to="/awards" 
              className="hidden sm:flex items-center gap-2 text-[#D4AF37] hover:text-[#E5C048] transition-colors"
              data-testid="view-all-awards"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
                  <div className="h-56 skeleton" />
                  <div className="p-5 space-y-3">
                    <div className="h-6 skeleton rounded w-3/4" />
                    <div className="h-4 skeleton rounded w-1/2" />
                    <div className="h-4 skeleton rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bento-grid">
              {featuredAwards.map((award, index) => (
                <AwardCard 
                  key={award.id} 
                  award={award} 
                  featured={index === 0}
                />
              ))}
            </div>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Link to="/awards">
              <Button 
                variant="outline" 
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                data-testid="mobile-view-all"
              >
                View All Awards
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Tiers Explanation Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#121212]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-4">
              Quality Recognition
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white">
              Award Tiers
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { tier: 'gold', title: 'Gold Award', description: 'The pinnacle of excellence. Reserved for establishments that exceed every expectation and set new standards in luxury travel.' },
              { tier: 'silver', title: 'Silver Award', description: 'Outstanding quality and service. Recognizing properties that consistently deliver exceptional experiences.' },
              { tier: 'bronze', title: 'Bronze Award', description: 'Commendable excellence. Awarded to establishments showing remarkable quality and guest satisfaction.' }
            ].map((item) => (
              <div
                key={item.tier}
                className="text-center p-8 rounded-xl border border-white/5 bg-[#0A0A0A]"
                data-testid={`tier-info-${item.tier}`}
              >
                <div className="flex justify-center mb-6">
                  <TierBadge tier={item.tier} size="lg" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-6">
            Know an Extraordinary Place?
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Submit your nomination for the next Travel Awards. Help us recognize exceptional hospitality around the world.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] font-semibold px-10 h-12"
              data-testid="cta-nominate-btn"
            >
              Submit a Nomination
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
